import { DEVELOPMENT } from "@/config/config";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { logout as apiLogout } from "../api/auth.api";
import { User } from "@/types";
import CryptoJS from 'crypto-js';
import { ENCRYPTION_KEY } from "@/api/config";

// Spark's opening message — introduces what it can actually do now that it
// reasons over live platform data (via MCP tools) instead of guessing.
export const SPARK_WELCOME_MESSAGE = `👋 Hi, I'm **Spark** — your Idearify AI co-pilot. I can look up live data on the platform to help you:

- **Refine your idea** — sharpen the pitch and find your edge
- **Find collaborators** — match with people who complement your skills
- **See what's already out there** — compare your idea to others on Idearify
- **Catch what's trending** — see what's active in the community right now

Tell me what you're working on, or tap a suggestion below to get started.`;

const encryptedStorage = {
  getItem: (name: string): string | null => {
    const raw = localStorage.getItem(name);
    if (!raw) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(raw, ENCRYPTION_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return decrypted || raw;
    } catch {
      return raw;
    }
  },
  setItem: (name: string, value: string): void => {
    const encrypted = CryptoJS.AES.encrypt(value, ENCRYPTION_KEY).toString();
    localStorage.setItem(name, encrypted);
  },
  removeItem: (name: string): void => {
    localStorage.removeItem(name);
  },
};

// Mock user for development
const mockUser: User = {
  id: "1",
  username: "demo_user",
  firstName: '',
  lastName: '',
  email: "demo_user@gmail.com",
  avatar: '',
  verified: true, // Retained field flag
  joinDate: new Date("2026-07-27T19:20:45.920Z"),
  ideas: 0,
  views: 0,
  followers: 0,
  following: 0,
  bio: "Innovation enthusiast and idea creator. Passionate about turning creative concepts into reality through technology and collaboration.",
  banner: 'demo_user',
  dateJoined: "2026-07-27T19:20:45.920Z",
  totalViews: 0,
  subscribers: [],
  subscribed: [],
  displayName: "demo_user",
  planType: "pro_monthly:prm1",
  monthlyUsage: {
    ideaPosts: -1,
    aiCredits: 500,
    month: "2026-07",
  },
};

export interface SparkChatMessage {
  id: string;
  sender: "user" | "ai";
  text: string;
  timestamp: string;
}

export interface AppState {
  user: User | null;
  isLoggedIn: boolean;
  token: string | null;
  hasSeenTutorial: boolean;
  runTutorial: boolean;
  setUser: (user: User | null) => void;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  setToken: (token: string | null) => void;
  setHasSeenTutorial: (hasSeen: boolean) => void;
  setRunTutorial: (run: boolean) => void;
  logout: () => Promise<void>;
  isBlocked: boolean;
  setIsBlocked: (blocked: boolean) => void;
  checkAuthStatus: (session: any) => boolean;
  initializeMockUser: () => void;
  currentCategory: string;
  setCurrentCategory: (category: string) => void;
  sparkChatMessages: SparkChatMessage[];
  setSparkChatMessages: (messages: SparkChatMessage[] | ((prev: SparkChatMessage[]) => SparkChatMessage[])) => void;
  clearSparkChatMessages: () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoggedIn: false,
      isBlocked: false,
      token: null,
      hasSeenTutorial: false,
      runTutorial: false,
      setUser: (user) => set({ user }),
      setIsLoggedIn: (isLoggedIn) => set({ isLoggedIn }),
      setToken: (token) => set({ token }),
      setIsBlocked: (isBlocked) => set({ isBlocked }),
      setHasSeenTutorial: (hasSeen) => set({ hasSeenTutorial: hasSeen }),
      setRunTutorial: (run) => set({ runTutorial: run }),
      currentCategory: "other",
      setCurrentCategory: (category) => set({ currentCategory: category }),
      sparkChatMessages: [
        {
          id: "1",
          sender: "ai",
          text: SPARK_WELCOME_MESSAGE,
          timestamp: new Date().toISOString(),
        },
      ],
      setSparkChatMessages: (messages) =>
        set((state) => ({
          sparkChatMessages:
            typeof messages === "function" ? messages(state.sparkChatMessages) : messages,
        })),
      clearSparkChatMessages: () =>
        set({
          sparkChatMessages: [
            {
              id: "1",
              sender: "ai",
              text: SPARK_WELCOME_MESSAGE,
              timestamp: new Date().toISOString(),
            },
          ],
        }),
      logout: async () => {
        try {
          await apiLogout();
        } catch (e) {
          console.error("Logout API call failed:", e);
        }
        localStorage.removeItem("jwt");
        localStorage.removeItem("refreshToken");
        set({ user: null, isLoggedIn: false, token: null });
      },
      checkAuthStatus: (session) => {
        const token = localStorage.getItem("jwt");
        const refreshToken = localStorage.getItem("refreshToken");
        const hasValidTokens = !!(token && refreshToken);

        if (hasValidTokens && !get().isLoggedIn) {
          const userData = session ? session() : null;
          set({ isLoggedIn: true, token, user: userData });
        }
        // With HttpOnly cookies, localStorage won't have tokens, so we MUST NOT
        // aggressively log the user out here just because hasValidTokens is false.
        // We will rely on apiClient.ts returning 401s to handle real session expiry.

        return get().isLoggedIn;
      },
      initializeMockUser: () => {
        // Only initialize mock user in development
        if (
          process.env.NODE_ENV === "development" &&
          DEVELOPMENT &&
          !get().user
        ) {
          set({ user: mockUser, isLoggedIn: true, token: "mock-token" });
        }
      },
    }),
    {
      name: 'idearify-app-storage',
      storage: createJSONStorage(() => encryptedStorage), // handles encryption both ways
      partialize: (state) => ({
        user: state.user,
        isLoggedIn: state.isLoggedIn,
        token: state.token,
        hasSeenTutorial: state.hasSeenTutorial,
        sparkChatMessages: state.sparkChatMessages,
      }),
    }
  )
);
